import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { RevealLines } from "@/components/Reveal";

const Cart = () => {
  const { items, removeItem, updateQty, total } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const checkout = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email for your order confirmation");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        email,
      };
      const { data } = await api.post("/checkout", payload);
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="pt-[72px] min-h-screen">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
        <RevealLines tag="h1" immediate lines={["Your Bag"]} className="font-serif text-7xl md:text-[8rem] leading-[0.85] tracking-tighter text-bone mb-14" />

        {items.length === 0 ? (
          <div className="py-20">
            <p className="font-serif text-4xl text-ash italic">Your bag is empty.</p>
            <Link to="/shop" className="btn-fill inline-flex items-center gap-3 px-10 py-4 overline text-sm mt-8">
              Discover the Collection <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-8 border-t border-line">
              {items.map((i) => (
                <div key={i.key} className="flex gap-6 py-8 border-b border-line" data-testid={`cart-row-${i.product_id}`}>
                  <div className="w-28 h-40 bg-ink-800 overflow-hidden shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex justify-between">
                      <div>
                        <h3 className="font-serif text-3xl text-bone">{i.name}</h3>
                        <p className="font-mono text-xs text-ash mt-2">SIZE {i.size}</p>
                      </div>
                      <button onClick={() => removeItem(i.key)} className="text-ash hover:text-crimson h-fit">
                        <X size={20} weight="thin" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-line">
                        <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-3 text-ash hover:text-bone"><Minus size={12} /></button>
                        <span className="font-mono text-sm w-10 text-center text-bone">{i.quantity}</span>
                        <button onClick={() => updateQty(i.key, i.quantity + 1)} className="p-3 text-ash hover:text-bone"><Plus size={12} /></button>
                      </div>
                      <p className="font-serif text-2xl text-bone">{formatINR(i.price * i.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-4">
              <div className="border border-line p-8 sticky top-28">
                <p className="overline mb-8">Order Summary</p>
                <div className="flex justify-between text-ash mb-4"><span>Subtotal</span><span className="text-bone">{formatINR(total)}</span></div>
                <div className="flex justify-between text-ash mb-6"><span>Shipping</span><span className="text-bone">Free</span></div>
                <div className="w-full h-px bg-line mb-6" />
                <div className="flex justify-between items-end mb-6">
                  <span className="overline">Total</span>
                  <span className="font-serif text-4xl text-bone">{formatINR(total)}</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="cart-checkout-email"
                  placeholder="Email for order confirmation"
                  className="w-full bg-transparent border border-line px-3 py-2.5 text-bone focus:border-crimson outline-none font-mono text-xs mb-4"
                />
                <button onClick={checkout} disabled={loading} data-testid="cart-checkout-button" className="btn-fill w-full py-4 flex items-center justify-center gap-3 overline text-sm disabled:opacity-50">
                  {loading ? "Redirecting…" : "Checkout"} <ArrowRight size={16} />
                </button>
                <p className="font-mono text-[10px] text-ash mt-4 text-center">SECURE PAYMENT VIA STRIPE · INR</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
